import unescape from 'lodash/unescape'
import isBetween from 'dayjs/plugin/isBetween'
import dayjs from 'dayjs'

import { SEARCH_ADS_INTEL_COUNTRY_CODES } from '@/utils/country/country-codes'
import { globalProperties as app } from '@/main'
import maDate from '@/base/MaDate'
import Store from '@/base/constants/Store'
import { RouteName } from '@/base/constants/route'
import { production, SA_INTEG_CALLBACK_URL } from '@/base/constants/ConfigVars'
import { maLogger } from './logger'

dayjs.extend(isBetween)

export const SEC_IN_MS = 1000
export const MIN_IN_MS = 60 * SEC_IN_MS
export const HOUR_IN_MS = 60 * MIN_IN_MS
export const DAY_IN_MS = 24 * HOUR_IN_MS
export const ISO_DATE_FORMAT = 'YYYY-MM-DD'
export const ISO_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
export const SHORT_DATE_FORMAT = 'MMM DD'
export const SHORT_TIME_FORMAT = 'h:mm A'
export const TEST_USER_HASH = -643103943

export const SA_INTEL_INTEG_CALLBACK_URL = SA_INTEG_CALLBACK_URL || (!production
  ? 'https://insights-testing.mobileaction.co/auth/apple-integ/callback' // for localhost: https://www.mobileaction.co/localhost-callback-apple-integ
  : `https://insights.mobileaction.co/auth/apple-integ/callback`)

const NUMERIC_SYMBOLS = ['k', 'm', 'b', 't']

/**
 * Return a proxied (if need be) and resized (if given) app icon url
 * @param store for which store icon belongs
 * @param url   icon url
 * @param width optional new width of icon
 * @returns {*} generated icon url
 */
export const DUMMY_IMG_SRC = `${import.meta.env.VITE_APP_MA_CDN_BASE_URL}/public/icons/ma-on-error-icon.png`
export const APP_STORE_IMG = `${import.meta.env.VITE_APP_MA_CDN_BASE_URL}/public/img/icons/App-Store-Icon.png`
export const GOOGLE_PLAY_IMG = `${import.meta.env.VITE_APP_MA_CDN_BASE_URL}/public/img/icons/Google-Play-Icon.png`

// from: https://stackoverflow.com/a/7616484
export function strHash(s) {
  if (typeof s !== 'string') {
    return 0
  }

  let hash = 0
  for (let i = 0; i < s.length; i++) {
    // bit or converts to 32bit integer
    hash = (((hash << 5) - hash) + s.charCodeAt(i)) | 0
  }
  return hash
}

export function generateIconUrl(store, url, width) {
  if (!url) {
    return DUMMY_IMG_SRC
  }
  // return data images and local images as is
  if (url.startsWith('data:image') || /^\/img\//.test(url)) {
    return url
  }
  // if width given set to that width
  if (width && !Number.isNaN(width)) {
    url = store === Store.Ios
      ? url.replace(/\d+x\d+bb\.jpg$/, `${width}x${width}bb.jpg`)
      : url.replace(/w\d+$/, `w${width}`)
  }

  // if already safe image return as is
  if (url.startsWith('https')) {
    return url
  }

  if (store === Store.Play && url.includes('ytimg.com')) { // to print on pdf use app-image as proxy
    maLogger.warn('proxy image needed:', store, url)
  }

  // unspecified protocol to https
  if (/^\/\//.test(url)) {
    return `https:${url}`
  }
  if (/^http:\/\/is\d+\.mzstatic/.test(url)) {
    return url.replace(/http:\/\/is(\d+)\.mzstatic/, 'https://is$1-ssl.mzstatic') // convert to ssl
  }
  if (store === Store.Ios) {
    maLogger.warn('proxy image needed:', store, url)
  }
  return url
}

export function numericFormatter(val, precision = 1, trailingZero = true) {
  val = Number(val) || 0
  let m
  for (let i = NUMERIC_SYMBOLS.length; i > 0; --i) {
    m = 1000 ** i
    if (val / m > 1) {
      return app.$highcharts.numberFormat(val / m, precision) + NUMERIC_SYMBOLS[i - 1]
    }
  }
  const formatted = app.$highcharts.numberFormat(val, precision)
  return trailingZero ? formatted : formatted.replace(/\.0*$/, '')
}

export function shortenAppName(appName, delim) {
  const maxLength = 50
  const splitLength = 25

  if (!appName) {
    return ''
  }

  const delimTemp = delim || ' -'

  if (appName.length > splitLength) {
    appName = appName.includes(delimTemp)
      ? appName.split(delimTemp)[0]
      : appName.split(':')[0]

    if (appName.length > maxLength) {
      const trimmedString = appName.substr(0, maxLength)
      appName = trimmedString.substr(0, Math.min(trimmedString.length, trimmedString.lastIndexOf(' ')))
    }
  }

  return appName
}

export const BOM = '\uFEFF'

/**
 *
 * @param filename
 * @param text
 * @param analyticsDownloadLocation - For analytics download event location tracking purposes.
 */
export function downloadAsFile(filename, text, analyticsDownloadLocation) {
  app.$analytics.downloadCsv(analyticsDownloadLocation)

  // add utf-8 bom if missing
  if (!text.startsWith(BOM)) {
    text = BOM + text
  }
  const pom = document.createElement('a')
  pom.setAttribute('href', `data:text/plain;charset=utf-8,${
    encodeURIComponent(text)}`)
  pom.setAttribute('download', filename)

  pom.style.display = 'none'
  document.body.appendChild(pom)

  pom.click()

  document.body.removeChild(pom)
}

function csvEscape(val) {
  const strVal = `${typeof val === 'undefined' ? '-' : val}`
  return strVal.search(/["\n,]/) >= 0 ? `"${strVal.replace(/"/g, '""')}"` : strVal
}

/**
 * Utility function that converts 2 dimensional array or array of objects to
 * csv text and calls downloadAsFile
 *
 * @param filename name of the file to be downloaded
 * @param data 2 dimensional array of values that can be converted to string
 * @param analyticsDownloadLocation - For analytics download event location tracking purposes.
 */
export function downloadDataAsCsv(filename, data, analyticsDownloadLocation) {
  let headers = null

  downloadAsFile(filename, data.map((row) => {
    if (Array.isArray(row)) {
      return row.map(csvEscape).join(',')
    }
    else {
      if (!headers) { // ensure same order by storing first headers
        headers = Object.keys(row)
      }
      return headers.map(k => csvEscape(row[k])).join(',')
    }
  }).join('\n'), analyticsDownloadLocation)
}

export function addCommas(value) {
  const x = (`${value}`).split('.')
  // http://stackoverflow.com/a/2901298
  const x1 = x[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, ',')
  const x2 = x.length > 1 ? `.${x[1]}` : ''
  return x1 + x2
}

export function fillMissingDatesForDateRange(data, startDate, endDate) {
  startDate = maDate(startDate).format(ISO_DATE_FORMAT)
  endDate = maDate(endDate).format(ISO_DATE_FORMAT)

  const diff = maDate(endDate).diff(maDate(startDate), 'days')

  const dateDataMap = {}

  for (let di = 0; di <= diff; di += 1) {
    dateDataMap[maDate(startDate).add(di, 'days').format(ISO_DATE_FORMAT)] = null
  }

  const newData = []

  data.forEach(([date, value]) => {
    const ds = maDate(date).format(ISO_DATE_FORMAT) // this is for timestamp returning endpoints
    dateDataMap[ds] = value
  })

  for (let di = 0; di <= diff; di += 1) {
    const ds = maDate(startDate).add(di, 'days').format(ISO_DATE_FORMAT)
    newData.push([new Date(ds).getTime(), ds in dateDataMap ? dateDataMap[ds] : null])
  }

  return newData
}

export function toTitleCase(str) {
  return !str
    ? str
    : str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
}

export function cleanCategoryName(ctg) {
  if (!ctg) {
    return null
  }
  if (typeof ctg === 'string') {
    return toTitleCase(ctg.replace(/_/g, ' '))
  }
  if (typeof ctg.name === 'string') {
    return toTitleCase(ctg.name.replace(/_/g, ' '))
  }
  return null
}

// regex function for case sensitive replacing
// http://kevin.vanzonneveld.net
// +   original by: booeyOH
// +   improved by: Ates Goral (http://magnetiq.com)
// +   improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
// +   bugfixed by: Onno Marsman
// *     example 1: pregQuote("$40");
// *     returns 1: '\$40'
// *     example 2: pregQuote("*RRRING* Hello?");
// *     returns 2: '\*RRRING\* Hello\?'
// *     example 3: pregQuote("\\.+*?[^]$(){}=!<>|:");
// *     returns 3: '\\\.\+\*\?\[\^\]\$\(\)\{\}\=\!\<\>\|\:'

// export const pregQuote = str => `${ str }`.replace(/([\\.+*?[^\]$(){}=!<>|:])/g, '\\$1');

export function pregQuote(str) {
  const rep = str.replace(/([\\.+*?[^\]$(){}=!<>|:])/g, '\\$1')
  return new RegExp(`(${rep})`, 'gi')
}

const possible = 'abcdefghijklmnopqrstuvwxyz0123456789'

export function randomString(length = 8) {
  let s = ''

  for (let i = 0; i < length; ++i) {
    s += possible.charAt(Math.floor(Math.random() * possible.length))
  }
  return s
}

export function convertToClosest(date) {
  const d = new Date(date).getTime() // convert string/ts to date and get ts
  const mod = d % DAY_IN_MS
  return maDate(d - mod).tz('GMT').valueOf()
}

export function getVScoreColor(score) {
  if (!score || Number.isNaN(score)) {
    return 'dark'
  }
  else if (score > 80) {
    return 'green'
  }
  else if (score > 60) {
    return 'blue'
  }
  else if (score > 40) {
    return 'yellow'
  }
  else if (score > 20) {
    return 'orange'
  }
  return 'red'
}

// Returns the difference between first and second as in d3.js
// enter means new diff old
// exit means old diff new
export function diffArrays(_old, _new, matcher = (currentValue, checkValue) => currentValue === checkValue) {
  _new = _new || []
  _old = _old || []
  const common = _new.filter(v => _old.findIndex(c => matcher(c, v)) > -1)
  return {
    common,
    enter: _new.filter(v => common.findIndex(c => matcher(c, v)) === -1),
    exit: _old.filter(v => common.findIndex(c => matcher(c, v)) === -1),
  }
}

// Array extensions

export const getStoreLabelFromName = storeName => storeName === Store.Ios ? 'App Store' : 'Google Play'

export const isIos = trackId => /^\d+$/.test(`${trackId}`)

export const getStoreName = trackId => isIos(trackId) ? Store.Ios : Store.Play

// safe cast track id
export const safeTrackId = id => Number(id) || id

// long safe check
export const safeTrackIdEquals = (id1, id2) => safeTrackId(id1) === safeTrackId(id2)

export const safeAppEquals = (a1, a2) => a1 && a2 && safeTrackIdEquals(a1.trackId, a2.trackId)

export function isAppFree(app) {
  return app.priceValue === 0
}

export function isSameWord(word1, word2) {
  return typeof word1 === 'string' && typeof word2 === 'string' && word1.toLowerCase() === word2.toLowerCase()
}

export function getDateStringWithFormat(date, format) {
  if (typeof date === 'string' || typeof date === 'number') {
    return maDate(new Date(date)).format(format)
  }
  if (date instanceof Date) {
    return maDate(date).format(format)
  }
  if (date instanceof Array) {
    return maDate(new Date(date[0], date[1] - 1, date[2], date[3], date[4], date[5])).format(format)
  }
  return maDate(date).format(format)
}

export function getAppIconRedirectRoute(routeName, trackId, { paramOpts = {}, queryOpts = {} }) {
  return {
    name: routeName,
    params: {
      trackId,
      ...paramOpts,
    },
    query: {
      activeApp: trackId,
      ...queryOpts,
    },
  }
}

export function getAppProfileRoute(trackId, queryOpts = {}) {
  return getAppIconRedirectRoute(RouteName.APP_INTELLIGENCE_ANALYSIS_APP_PROFILE, trackId, { queryOpts })
}

export function getSearchByAppRoute(trackId, queryOpts = {}) {
  return getAppIconRedirectRoute(RouteName.SEARCH_ADS_INTELLIGENCE_AD_PLACEMENT_SEARCH_RESULT_APP, trackId, { queryOpts })
}

export function mergeDeep(target, source) {
  const isObject = item => item && typeof item === 'object' && !Array.isArray(item)
  const output = Object.assign({}, target)
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] })
        }
        else {
          output[key] = mergeDeep(target[key], source[key])
        }
      }
      else {
        Object.assign(output, { [key]: source[key] })
      }
    })
  }
  return output
}

/**
 * Sorts given collection by given key if collection is a false statement returns empty array
 *
 * @param array
 * @param key
 * @param order ascending or not
 * @returns {*}
 */
export function sortByKey(array, key, order = 'ascending') {
  if (!array || !Array.isArray(array)) {
    return array || []
  }
  const dir = order === 'ascending' ? -1 : 1
  return array.sort((a, b) => {
    const x = a[key]
    const y = b[key]
    return x === y ? 0 : (x < y ? dir : -dir)
  })
}

export function resolveObjectPath(obj = null, keyPath = null) {
  if (!keyPath || typeof keyPath !== 'string') {
    maLogger.warn('Invalid key for resolveObjectPath:', keyPath)
    return null
  }
  const keys = keyPath.split('.')
  let currObj = obj
  for (let i = 0; i < keys.length; i += 1) {
    if (!currObj) {
      return currObj
    }
    currObj = currObj[keys[i]]
  }
  return currObj
}

export function updateRouteQuery(v, pageQuery = {}, pageParams = {}, noRefresh = true) {
  const query = Object.assign({}, v.$route.query, pageQuery)
  const params = Object.assign({}, v.$route.params, pageParams)
  // delete keys with null or undefined values
  Object.keys(query).forEach((key) => {
    if (query[key] === null || typeof query[key] === 'undefined') {
      delete query[key]
    }
  })
  if ((generateFetchKey(query) === generateFetchKey(v.$route.query)) && (generateFetchKey(params) === generateFetchKey(v.$route.params))) {
    return
  }
  v.$router.replace({
    query,
    params: { noRefresh, ...params },
  })
}
// end temporary utility methods

// builds and returns array of zones to display null values as dotted lines on chart
// zones example: http://jsfiddle.net/gh/get/jquery/1.7.2/highslide-software/highcharts.com/tree/master/samples/highcharts/series/color-zones-dashstyle-dot
export function buildZones(d, lineStyle) {
  const values = d.map(dd => dd[1])
  const dates = d.map(dd => dd[0])

  const zones = []
  const len = values.length
  let current, previous, dashStyle, value

  // find first non-null
  let i
  for (i = 0; i < len && values[i] === null; i += 1) {
    /* empty */
  }
  zones.push({ value: i })

  while (++i < len) {
    previous = values[i - 1]
    current = values[i]
    dashStyle = lineStyle || ''

    if (previous !== null && current === null) {
      dashStyle = 'solid'
      value = dates[i - 1]
    }
    else if (previous === null && current !== null) {
      if (lineStyle === 'solid') {
        dashStyle = 'solid'
      }
      else {
        dashStyle = 'dot'
      }

      value = dates[i]
    }

    if (dashStyle) {
      zones.push({
        dashStyle,
        value,
      })
    }
  }

  return zones
}

export function htmlEscape(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\//g, '&#x2F;')
}

export function trimKeyword(kw) {
  return !kw || !kw.length
    ? kw
    : kw.replace(/İ/g, 'i').trim().toLowerCase().replace(/,/g, '').replace(/\\s+/g, ' ')
}

export const safeTrim = str => (str || '').trim()

/**
 * Converts given params to query string
 * @param params object mapping keys to values
 * @param keepComma list of keys which can keep their commas as , instead of %2C
 * @returns {string} query string starting with ?
 */
export function paramsToQuery(params, keepComma = ['rating']) {
  if (typeof params !== 'object') {
    return ''
  }
  const _keepComma = keepComma || []
  const encode = (k) => {
    const encoded = encodeURIComponent(params[k])
    return _keepComma.includes(k) ? encoded.replace(/%2C/g, ',') : encoded
  }

  return Object.keys(params)
    .filter(k => typeof params[k] !== 'undefined' && params[k] !== null) // undefined or null shouldn't be sent
    .map(k => `${k}=${encode(k)}`)
    .join('&')
}

export function customSplit(str) {
  if (!str || typeof str !== 'string') {
    return str
  }
  if (str.includes('、')) { // japanese i  guess
    str = str.replace(/、/g, ',')
  }
  if (str.includes('،')) { // arabic comma
    str = str.replace(/،/g, ',')
  }
  if (str.includes('，')) {
    str = str.replace(/，/g, ',')
  }
  return str.split(',')
}

/**
 * This function removes any HTML tags from a given string by first unescaping any escaped HTML entities
 * and then using a regular expression to replace the tags with an empty string.
 * this function is used for the input text cases that we cannot use v-html
 * @param inputText Object to be cleared from HTML tags
 * @returns {string}
 */
export function clearRegexFromInput(inputText) {
  return String(unescape(inputText)).replace(/<[^>]+>/g, '')
}

/**
 * Checks if account have any limitations using error codes from a failed request
 * @param errors list of string error codes
 * @returns {boolean|*}
 */
export function needsFullAccess(errors) {
  if (!errors || !Array.isArray(errors)) {
    return false
  }
  return errors.includes('COUNTRY_NOT_ALLOWED')
    || errors.includes('APP_NOT_ALLOWED')
    || errors.includes('KEYWORD_NOT_ALLOWED')
    || errors.includes('CATEGORY_NOT_ALLOWED')
}

/**
 * Sorts given object keys to produce consistent results
 * @param o original object
 * @param processField field processing function with signature processField(key, value) for doing custom processing
 * @returns {{}|*}
 */
export function sortObjectKeys(o, processField) {
  if (!o || !['object', 'array'].includes(typeof o)) {
    return o
  }
  if (Array.isArray(o)) {
    return o.map(v => sortObjectKeys(v))
  }
  // default field to value mapper
  const _processField = processField && typeof processField === 'function' ? processField : (k, v) => v

  return Object.keys(o).sort().reduce((r, k) => {
    r[k] = o[k] && typeof o[k] === 'object' ? sortObjectKeys(o[k]) : _processField(k, o[k])
    return r
  }, {})
}

/**
 * Returns a sorted keyed json representation of the object. This can be used for fetchKey generation from params.
 * If there are nested objects, objects are filtered through the generateFetchKey as well
 * @param obj Object to be converted to key string
 * @returns {string}
 */
export function generateFetchKey(obj) {
  return JSON.stringify(sortObjectKeys(obj, (k, v) => {
    if (k === 'trackId') {
      return `${v}`
    }
    if (v && maDate.isMaDate(v)) {
      return v.format(ISO_DATE_FORMAT)
    }
    return v
  }))
}

/**
 * Shows if given date is new
 * @param dateStr date of format ISO_DATE_FORMAT
 * @returns {boolean}
 */
export const isNew = dateStr => maDate(dateStr, ISO_DATE_FORMAT).diff(maDate(), 'days') > 0

/**
 * Generates auth callback url
 * @param type type of auth
 * @param forwardTo the path that will be forwarded to
 * @returns {string}
 */
export function getAuthRedirectUri(type, forwardTo = null) {
  const baseUri = `${window.origin}/auth/${type}/callback`
  return forwardTo === null ? baseUri : `${baseUri}?${paramsToQuery({ forwardTo })}`
}

/**
 * Generates output in terms of day, hour, minute, and second
 * @param seconds type of number
 * @returns {string}
 */
export function getTimeString(seconds) {
  const days = Math.floor(seconds / (3600 * 24))
  const hours = Math.floor((seconds % (3600 * 24)) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secondsLeft = Math.floor(seconds % 60)
  return [
    days > 0 ? `${days}d` : '',
    hours > 0 ? `${hours}h` : '',
    minutes > 0 ? `${minutes}m` : '',
    secondsLeft > 0 ? `${secondsLeft}s` : '',
  ].filter(v => v).join(' ')
}

/**
 * Generates output in terms of day, hour, minute, and second
 * @param hours type of number
 * @returns {string}
 */
export function getDayFromHours(hours) {
  const currentTime = new Date()

  const pastDate = new Date(currentTime.getTime() - hours * 3600000)

  const diffMilliseconds = currentTime - pastDate

  const daysDiff = Math.floor(diffMilliseconds / (1000 * 60 * 60 * 24))
  const hoursDiff = Math.floor((diffMilliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutesDiff = Math.floor((diffMilliseconds % (1000 * 60 * 60)) / (1000 * 60))

  return [
    daysDiff > 0 ? `${daysDiff}d` : '',
    hoursDiff > 0 ? `${hoursDiff}h` : '',
    minutesDiff > 0 ? `${minutesDiff}m` : '',
  ].filter(v => v).join(' ')
}

/**
 * Returns a shortened text based on the given max size
 */
export function shorten(text, max) {
  return text?.length > max ? text.slice(0, max).split(' ').slice(0, -1).join(' ') : text
}

/***
 * Returns partitions of list according to size.
 * E.g: input = [1,2,3,4,5,6,7,8,9] -> output = [[1,2,3],[4,5,6],[7,8,9]]
 * @param inputArr
 * @param size
 * @returns {*[]} partitions of array
 */
export function partitionsOfArray(inputArr, size) {
  const output = []

  for (let i = 0; i < inputArr.length; i += size) {
    output[output.length] = inputArr.slice(i, i + size)
  }

  return output
}

// under_score to lowerCamelCase
export function underscoreToCamelCase(word) {
  return word && word.split('_')
    .map((word, index) => index > 0
      ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      : word.toLowerCase())
    .join('')
}

export const SCHEDULE_DEMO_URL = 'https://www.mobileaction.co/schedule-demo/'
export const redirectScheduleDemoPage = () => window.open(SCHEDULE_DEMO_URL, '_blank')

/**
 * Returns given publisher's name.
 * @param {object} publisher - Publisher DTO
 * @returns {string} publisher name
 */
export const getPublisherName = publisher => publisher.developerName ? publisher.developerName : publisher.developerId

/**
 * Returns app model.
 * @param {string|number} activeAppTrackId - Active app track id
 * @param {object} app - Target app
 * @returns {object} App DTO
 */
export function appDto({ activeAppTrackId, app }) {
  const store = getStoreName(app.trackId)
  const categoryWithStoreNames = `${cleanCategoryName(app.categoryName)} · ${
    store == Store.Ios
      ? 'App Store'
      : 'Play Store'
  }`

  return Object.assign(app, {
    store,
    id: app.trackId, // for virtual scroll
    name: app?.name || 'Removed App',
    iconUrl: generateIconUrl(app.storeName, app.iconUrl),
    competitors: app.competitors || [],
    competitorCount: app.competitors?.length ?? 0,
    integrated: app.isStoreAccountConnected,
    selected: safeTrackIdEquals(activeAppTrackId, app.trackId),
    developerName: app?.developerName || app?.developerId,
    integrationTooltip: app.isStoreAccountConnected
      ? 'App is integrated, click to see status.'
      : 'App is not integrated, click to integrate.',
    categoryWithStoreNames,
  })
}

/**
 * Returns app model.
 * @param {string | number} trackId - Active app track id
 * @param {string} countryCode
 * @returns {string} Store URL
 */
export function getStoreUrl(trackId, countryCode) {
  return getStoreName(trackId) === Store.Ios
    ? `https://itunes.apple.com/${countryCode}/app/id${trackId}`
    : `https://play.google.com/store/apps/details?${paramsToQuery({ gl: countryCode, id: trackId })}`
}

/**
 * camelCase to Normal Case.
 */
export function toNormalCase(camelCaseText) {
  return camelCaseText
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (str) => {
      return str.toUpperCase()
    })
}

/**
 * Formats a date range into a string in the format 'Sep 20 - 23, 2023'.
 *
 * @param {string} start - The start date in format 'YYYY-MM-DD' (e.g., '2023-09-20').
 * @param {string} end - The end date in format 'YYYY-MM-DD' (e.g., '2023-09-23').
 * @returns {string} A string representation of the date range in the specified format.
 */
export function humanReadableDateRange(start, end) {
  const startDate = dayjs(start)
  const endDate = dayjs(end)
  if (startDate.month() !== endDate.month() || startDate.year() !== endDate.year()) {
    return `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`
  }
  return `${startDate.format('MMM D')} - ${endDate.format('D, YYYY')}`
}

/**
 * Checks if a given date falls within a specified date range.
 *
 * @param {string|Date} dateToCheck - The date to check, either as a string or a Date object.
 * @param {string|Date} startDate - The start date of the range, either as a string or a Date object.
 * @param {string|Date} endDate - The end date of the range, either as a string or a Date object.
 * @param {boolean} includeStartAndEnd -
 * @returns {boolean} - Returns true if the date is within the specified range, otherwise false.
 */
export function isDateInRange(dateToCheck, startDate, endDate, includeStartAndEnd = true) {
  const inclusivity = includeStartAndEnd ? '[]' : '()'
  return dayjs(dateToCheck).isBetween(startDate, endDate, 'day', inclusivity)
}

/**
 * returns an array containing all dates between two given dates.
 *
 * @param {string|Date} startDate - Start date (in YYYY-MM-DD format or as a Date object).
 * @param {string|Date} endDate - End date (in YYYY-MM-DD format or as a Date object).
 * @returns {Array} - Array containing dates between the start and end dates.
 */
export function getDatesBetween(startDate, endDate, includeEndDate = false) {
  const datesArray = []

  const start = dayjs(startDate)
  const end = includeEndDate ? dayjs(endDate).add(1, 'days') : dayjs(endDate)

  if (start.isAfter(end)) {
    maLogger.warn('Error: Start date is after end date.')
    return datesArray
  }

  let currentDate = start

  while (currentDate.isBefore(end) || currentDate.isSame(end)) {
    datesArray.push(currentDate.format('YYYY-MM-DD'))
    currentDate = currentDate.add(1, 'day')
  }

  return datesArray
}

export function checkPrice(app) {
  return isAppFree(app) ? 'Free' : app.price
}

// visibility scores are calculated every sunday
export function getLastSunday(date) {
  const today = dayjs(date)
  const todayDayOfWeek = today.day()
  const lastSunday = today.subtract(todayDayOfWeek === 0 ? 7 : todayDayOfWeek, 'day')
  return maDate(lastSunday).formatISODate()
}

export function isSearchScoreModalAvailable(trackId, countryCode) {
  return getStoreName(trackId) === Store.Ios && SEARCH_ADS_INTEL_COUNTRY_CODES.includes(countryCode)
}

export function getRelevancyScoreDisplayText(score) {
  switch (score) {
    case 1:
      return 'High'
    case 2:
      return 'Medium'
    case 3:
      return 'Low'
    default:
      return 'Lowest'
  }
}

export function hexToRgb(hex) {
  const _hex = hex.replace('#', '') // Remove the # character, if present
  const r = Number.parseInt(_hex.substring(0, 2), 16)
  const g = Number.parseInt(_hex.substring(2, 4), 16)
  const b = Number.parseInt(_hex.substring(4, 6), 16)
  return { r, g, b } // Returns the RGB color value as an object
}

export function lightenRgbaColor(hex, opacity = 1, factor = 0.2) {
  let { r, g, b } = hexToRgb(hex)

  r = Math.min(255, Math.floor(r + (255 - r) * factor))
  g = Math.min(255, Math.floor(g + (255 - g) * factor))
  b = Math.min(255, Math.floor(b + (255 - b) * factor))

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

/**
 * Returns the pluralized version of a word based on the provided count.
 * If count is less than 2, it returns the word as-is.
 *
 * @param {string} word - The word to be pluralized.
 * @param {number} count - The count used to determine if pluralization is needed.
 * @returns {string} - The pluralized version of the word.
 */
export function pluralizeWord(word, count) {
  if (count < 2)
    return word

  if (word.endsWith('y'))
    return `${word.slice(0, -1)}ies`

  if (word.endsWith('s') || word.endsWith('x') || word.endsWith('z') || word.endsWith('sh') || word.endsWith('ch'))
    return `${word}es`

  return `${word}s`
}
